/**
 * Medical Terms Mapping
 * Maps technical medical terms to user-friendly French descriptions
 * Used for symptom display in patient check-in and clinical evaluation
 */

// Symptom mappings - technical term -> user-friendly display
export const SYMPTOM_DISPLAY_NAMES = {
  // Digestive symptoms
  nausea: "Nausées / Envie de vomir",
  vomiting: "Vomissements",
  diarrhea: "Diarrhée",
  constipation: "Constipation",
  mucite: "Douleurs bouche / Aphtes",
  mucositis: "Douleurs bouche / Aphtes", // Alias
  appetite_loss: "Perte d'appétit",
  
  // Neurological symptoms
  neuropathy: "Fourmillements / Engourdissements",
  peripheral_neuropathy: "Fourmillements (mains/pieds)",
  headache: "Maux de tête",
  dizziness: "Vertiges / Étourdissements",
  confusion: "Confusion / Troubles mémoire",
  
  // Pain symptoms
  pain: "Douleur",
  abdominal_pain: "Douleur abdominale",
  chest_pain: "Douleur thoracique",
  bone_pain: "Douleur osseuse",
  joint_pain: "Douleur articulaire",
  
  // Skin symptoms
  rash: "Éruption cutanée / Boutons",
  skin_rash: "Éruption cutanée",
  hand_foot_syndrome: "Syndrome main-pied (rougeurs, cloques)",
  alopecia: "Perte de cheveux",
  dry_skin: "Peau sèche",
  itching: "Démangeaisons",
  
  // Fatigue and general
  fatigue: "Fatigue intense",
  asthenia: "Fatigue / Faiblesse",
  fever: "Fièvre",
  chills: "Frissons",
  weight_loss: "Perte de poids",
  weight_gain: "Prise de poids",
  edema: "Gonflement (jambes, visage)",
  
  // Respiratory symptoms
  dyspnea: "Essoufflement / Difficultés respiratoires",
  cough: "Toux",
  shortness_of_breath: "Manque de souffle",
  
  // Cardiac symptoms
  palpitations: "Palpitations / Cœur qui bat fort",
  tachycardia: "Cœur rapide",
  hypertension: "Tension élevée",
  hypotension: "Tension basse",
  
  // Mental health
  anxiety: "Anxiété / Inquiétude",
  depression: "Tristesse / Déprime",
  insomnia: "Troubles du sommeil",
  mood_changes: "Changements d'humeur",
  
  // Other
  bleeding: "Saignements",
  bruising: "Bleus / Ecchymoses",
  infection: "Signes d'infection",
  allergic_reaction: "Réaction allergique",
};

// Get display name for a symptom, with fallback to original
export function getSymptomDisplayName(technicalTerm) {
  if (!technicalTerm) return "";
  const normalized = technicalTerm.toLowerCase().replace(/[- ]/g, "_");
  return SYMPTOM_DISPLAY_NAMES[normalized] || technicalTerm;
}

// Reverse mapping - get technical term from display name
export function getTechnicalTerm(displayName) {
  const entry = Object.entries(SYMPTOM_DISPLAY_NAMES).find(
    ([_, display]) => display.toLowerCase() === displayName.toLowerCase()
  );
  return entry ? entry[0] : displayName;
}

// Get both display and technical term for display
export function getSymptomWithTechnicalTerm(technicalTerm) {
  const displayName = getSymptomDisplayName(technicalTerm);
  if (displayName === technicalTerm) {
    return { display: technicalTerm, technical: technicalTerm };
  }
  return { display: displayName, technical: technicalTerm };
}

// Severity level display names (French)
export const SEVERITY_DISPLAY = {
  0: "Aucun",
  1: "Léger",
  2: "Modéré", 
  3: "Sévère",
  4: "Très sévère",
  none: "Aucun",
  mild: "Léger",
  moderate: "Modéré",
  severe: "Sévère",
  critical: "Très sévère"
};

// Cancer type display names
export const CANCER_TYPE_DISPLAY = {
  breast: "Cancer du sein",
  lung: "Cancer du poumon",
  colorectal: "Cancer colorectal",
  prostate: "Cancer de la prostate",
  ovarian: "Cancer de l'ovaire",
  pancreatic: "Cancer du pancréas",
  liver: "Cancer du foie",
  kidney: "Cancer du rein",
  bladder: "Cancer de la vessie",
  stomach: "Cancer de l'estomac",
  esophageal: "Cancer de l'œsophage",
  lymphoma: "Lymphome",
  leukemia: "Leucémie",
  melanoma: "Mélanome",
  other: "Autre"
};

// Treatment type display names
export const TREATMENT_TYPE_DISPLAY = {
  chemotherapy: "Chimiothérapie",
  radiotherapy: "Radiothérapie",
  immunotherapy: "Immunothérapie",
  targeted_therapy: "Thérapie ciblée",
  hormone_therapy: "Hormonothérapie",
  surgery: "Chirurgie",
  combined: "Traitement combiné",
  other: "Autre"
};

export default {
  SYMPTOM_DISPLAY_NAMES,
  getSymptomDisplayName,
  getTechnicalTerm,
  getSymptomWithTechnicalTerm,
  SEVERITY_DISPLAY,
  CANCER_TYPE_DISPLAY,
  TREATMENT_TYPE_DISPLAY
};
